function generateId() {
      return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  function isIOS() {
      return /iPhone|iPad/i.test(navigator.userAgent);
  }

  function isAndroid() {
      return /Android/i.test(navigator.userAgent);
  }

  function isNative() {
      if (isIOS()) {
          return !!(window.webkit?.messageHandlers?.JphIOS);
      }
      if (isAndroid()) {
          return typeof Jph !== 'undefined' && !!Jph;
      }
      return false;
  }

  // ─── LocalStorage fallback (browser) ───────────────────────────────────────

  const LS_PREFIX = 'tiger_sqlite_';

  function lsGetTable(tableName) {
      try {
          return JSON.parse(localStorage.getItem(LS_PREFIX + tableName) || '[]');
      } catch {
          return [];
      }
  }

  function lsSaveTable(tableName, rows) {
      localStorage.setItem(LS_PREFIX + tableName, JSON.stringify(rows));
  }

  function lsModifyTable(action, tableName) {
      if (action === 'create') {
          if (!localStorage.getItem(LS_PREFIX + tableName)) {
              lsSaveTable(tableName, []);
          }
          return Promise.resolve(true);
      }
      if (action === 'delete') {
          localStorage.removeItem(LS_PREFIX + tableName);
          return Promise.resolve(true);
      }
      return Promise.reject(new Error('Acción no soportada'));
  }

  function lsInsert(tableName, dataEntry) {
      try {
          const rows = lsGetTable(tableName);
          const newRow = { ...dataEntry, id: dataEntry.id ?? Date.now() };
          // Respetar PRIMARY KEY: reemplazar si ya existe un row con mismo id
          const idx = rows.findIndex(r => r.id === newRow.id);
          if (idx >= 0) {
              rows[idx] = newRow;
          } else {
              rows.push(newRow);
          }
          lsSaveTable(tableName, rows);
          return Promise.resolve(true);
      } catch (e) {
          console.error('[TigerSQLite lsInsert] Error:', e?.message, e?.name);
          return Promise.reject(e);
      }
  }

  function lsFetch(tableName, action, params) {
      const rows = lsGetTable(tableName);

      if (action === 'tableExists') {
          return Promise.resolve(localStorage.getItem(LS_PREFIX + tableName) !== null);
      }
      if (action === 'fetchAll') {
          return Promise.resolve(rows);
      }
      if (action === 'count') {
          if (!params) return Promise.resolve(rows.length);
          const filtered = rows.filter(row =>
              Object.entries(params).every(([k, v]) => String(row[k]) === String(v))
          );
          return Promise.resolve(filtered.length);
      }
      if (action === 'findByParam') {
          const filtered = rows.filter(row =>
              Object.entries(params).every(([k, v]) => String(row[k]) === String(v))
          );
          return Promise.resolve(filtered);
      }
      return Promise.reject(new Error('Acción no soportada'));
  }

  function lsUpdate(tableName, updatedData, whereClause, whereArgs) {
      const rows = lsGetTable(tableName);
      // Soporte básico: "campo = ?"
      const match = whereClause.match(/^(\w+)\s*=\s*\?$/);
      if (!match) return Promise.reject(new Error('whereClause no soportado en fallback'));
      const field = match[1];
      const value = whereArgs[0];
      const updated = rows.map(row =>
          String(row[field]) === String(value) ? { ...row, ...updatedData } : row
      );
      lsSaveTable(tableName, updated);
      return Promise.resolve(true);
  }

  function lsDeleteRecord(tableName, deleteCriteria) {
      let rows = lsGetTable(tableName);
      for (const criteria of deleteCriteria) {
          const match = criteria.whereClause.match(/^(\w+)\s*=\s*\?$/);
          if (!match) continue;
          const field = match[1];
          const value = criteria.whereArgs[0];
          rows = rows.filter(row => String(row[field]) !== String(value));
      }
      lsSaveTable(tableName, rows);
      return Promise.resolve(true);
  }

  // ─── Bridge nativo ──────────────────────────────────────────────────────────

  function modifyTable(action, tableName, fields) {
      if (!isNative()) return lsModifyTable(action, tableName);

      return new Promise((resolve, reject) => {
          const uniqueId = generateId();
          window["callback_" + uniqueId] = { resolve, reject };
          const params = { action, tableName, fields, callbackId: uniqueId };
          if (isIOS()) {
              window.webkit.messageHandlers.JphIOS.postMessage({ action: 'tigerModifyTable', params });
          } else {
              Jph.tigerModifyTable(JSON.stringify(params));
          }
      });
  }

  function insert(tableName, dataEntry = null) {
      if (!isNative()) return lsInsert(tableName, dataEntry || {});

      return new Promise((resolve, reject) => {
          const uniqueId = generateId();
          window["callback_" + uniqueId] = { resolve, reject };
          const params = {
              action: 'insertData',
              tableName,
              data: dataEntry !== null ? [dataEntry] : [],
              callbackId: uniqueId
          };
          if (isIOS()) {
              window.webkit.messageHandlers.JphIOS.postMessage({ action: 'tigerInsertDataDynamic', params });
          } else {
              Jph.tigerInsertDataDynamic(JSON.stringify(params));
          }
      });
  }

  // El bridge nativo puede devolver { table, action, result: [...] } — normalizar a array
  function normalizeNativeFetch(action, raw) {
      if (action === 'tableExists') return typeof raw === 'boolean' ? raw : !!(raw?.result);
      if (action === 'count') return typeof raw === 'number' ? raw : (raw?.result ?? 0);
      return Array.isArray(raw) ? raw : (Array.isArray(raw?.result) ? raw.result : []);
  }

  function fetch(tableName, action = 'fetchAll', params = null) {
      if (!isNative()) return lsFetch(tableName, action, params);

      return new Promise((resolve, reject) => {
          const uniqueId = generateId();
          window["callback_" + uniqueId] = {
              resolve: (raw) => resolve(normalizeNativeFetch(action, raw)),
              reject,
          };
          const data = { tableName, action, params, callbackId: uniqueId };
          if (isIOS()) {
              window.webkit.messageHandlers.JphIOS.postMessage({ action: 'tigerFetchTableData', params: data });
          } else {
              Jph.tigerFetchTableData(JSON.stringify(data));
          }
      });
  }

  function update(tableName, updatedData, whereClause, whereArgs) {
      if (!isNative()) return lsUpdate(tableName, updatedData, whereClause, whereArgs);

      return new Promise((resolve, reject) => {
          const uniqueId = generateId();
          window["callback_" + uniqueId] = { resolve, reject };
          const params = { action: 'update', tableName, updatedData, whereClause, whereArgs, callbackId: uniqueId };
          if (isIOS()) {
              window.webkit.messageHandlers.JphIOS.postMessage({ action: 'tigerUpdateTable', params });
          } else {
              Jph.tigerUpdateTable(JSON.stringify(params));
          }
      });
  }

  function deleteRecord(tableName, deleteCriteria) {
      if (!isNative()) return lsDeleteRecord(tableName, deleteCriteria);

      return new Promise((resolve, reject) => {
          const uniqueId = generateId();
          window["callback_" + uniqueId] = { resolve, reject };
          const params = { action: 'deleteRecord', tableName, deleteCriteria, callbackId: uniqueId };
          if (isIOS()) {
              window.webkit.messageHandlers.JphIOS.postMessage({ action: 'tigerDeleteRecord', params });
          } else {
              Jph.tigerDeleteRecord(JSON.stringify(params));
          }
      });
  }

  const TigerSQLite = { modifyTable, insert, fetch, update, deleteRecord };

  export default TigerSQLite;